import { computed, ref, shallowRef } from 'vue'
import { IExclusionZoneRepository } from '../../../../@core/exclusion-zone/domain/repos/IExclusionZone.repository'
import { IMonitoredAreaRepository } from '../../../../@core/monitored-area/domain/repositories/IMonitoredArea.repository'
import { MonitoredArea } from '../../../../@core/monitored-area/domain/entities/MonitoredArea'
import { useMessages } from '../../../../composables/useMessages'
import { ExclusionZone } from '../../../../@core/exclusion-zone/domain/entities/ExclusionZone'
import { ExclusionZoneTableColumnsFactory } from '../factories/ExclusionZoneTableColumns.factory'
import { ExportDataProvider } from '../../@shared/providers/ExportData.provider'
import { ExclusionZoneFormSchema } from '../schemas/ExclusionZoneForm.schema'
import router from '../../../../router'
import { useExclusionZoneStore } from '../stores/ExclusionZone.store'
import { bbox, feature, featureCollection } from '@turf/turf'
import { FeatureCollection, Geometry } from 'geojson'
import { useTable } from '../../../ui/components/baseTable/composables/Table.composable'
import { Map } from 'mapbox-gl'
import { GeoJSON } from 'geojson'

export class ExclusionZoneViewModel {
	constructor(
		private _exclusionZoneRepository: IExclusionZoneRepository,
		private _monitoredAreaRepository: IMonitoredAreaRepository,
	) {}

	private _addMessage = useMessages().addMessage

	openForm = ref(false)
	fetching = ref(false)
	showDeletePopup = ref(false)
	showExclusionZoneResume = ref(false)

	drawnFeatures = ref<FeatureCollection>()
	drawInstance = shallowRef<MapboxDraw>()
	mapInstance = shallowRef<Map>()

	fitBounds() {
		const data = this.drawnFeatures.value?.features?.length
			? this.drawnFeatures.value
			: this.geojson.value

		if (!data) return

		this.mapInstance.value?.fitBounds(bbox(data) as any, {
			padding: 20,
			maxZoom: 13,
		})
	}

	updateDrawnFeatures(draw?: MapboxDraw) {
		const featureCollection = draw?.getAll()

		if (featureCollection) {
			featureCollection.features.forEach((feature) => {
				feature.properties = { ...feature.properties, cor: '#F82027' }
			})
		}

		this.drawnFeatures.value = featureCollection
	}

	loadFeatureCollectionIntoDraw() {
		if (!this.exclusionZone.value.zones.length || !this.drawInstance.value) return
		this.drawInstance.value.deleteAll()

		this.drawnFeatures.value?.features.forEach((feature) => {
			try {
				this.drawInstance.value?.add(feature)
			} catch {
				const f = { ...feature } as any
				delete f.id
				this.drawInstance.value?.add(f)
			}
		})

		const fc = this.drawInstance.value.getAll()
		this.drawnFeatures.value = fc
	}

	setMapListeners({ map, draw }: { map: Map; draw?: MapboxDraw }) {
		map.on('draw.create', () => this.updateDrawnFeatures(draw))
		map.on('draw.update', () => this.updateDrawnFeatures(draw))
		map.on('draw.delete', () => this.updateDrawnFeatures(draw))

		this.mapInstance.value = map
		this.drawInstance.value = draw
		this.fitBounds()
	}

	monitoredAreas = computed({
		get() {
			return useExclusionZoneStore().monitoredAreas || []
		},
		set(value: MonitoredArea[]) {
			useExclusionZoneStore().monitoredAreas = value
		},
	})

	exclusionZones = ref<ExclusionZone[]>([])

	columns = computed(() => ExclusionZoneTableColumnsFactory.make(this.registry.sortOptions.value))

	public registry = useTable(this.exclusionZones)

	monitoredAreaOptions = computed(() =>
		this.monitoredAreas.value.map((area) => {
			return { label: area.name, value: area.id }
		}),
	)

	geojson = computed(() => {
		const monitoredArea = this.monitoredAreas.value.find(
			(monitoredArea) => monitoredArea.id === this.exclusionZone.value.monitoredAreaId,
		)

		const monitoredAreaFeature = feature(monitoredArea!.geojson)

		if (this.drawnFeatures.value?.features.length) {
			return featureCollection([
				monitoredAreaFeature,
				...this.drawnFeatures.value.features,
			]) as GeoJSON
		}

		return featureCollection([monitoredAreaFeature]) as GeoJSON
	})

	private _initState = {
		id: undefined,
		description: '',
		name: '',
		monitoredAreaId: '',
	}

	exclusionZone = computed({
		get() {
			return useExclusionZoneStore().exclusionZone
		},
		set(value: any) {
			useExclusionZoneStore().exclusionZone = value
		},
	})

	kebabOptions() {
		return [
			{
				icon: 'ic:round-edit',
				event: 'editar',
				name: 'Editar',
			},
		]
	}

	public resetState() {
		this.exclusionZone.value = { ...this._initState }
	}

	monitoredAreaName = computed(
		() =>
			this.monitoredAreas.value.find(
				(monitoredArea) => monitoredArea.id === this.exclusionZone.value.monitoredAreaId,
			)?.name,
	)

	async getMonitoredAreas() {
		try {
			const customerId = localStorage.getItem('customerId')
			const response = await this._monitoredAreaRepository.getSetted(customerId!)
			this.monitoredAreas.value = response
		} catch {
			this._addMessage({ description: 'Erro ao buscar áreas monitoradas', type: 'error' })
		}
	}

	async getExclusionZones() {
		try {
			const customerId = localStorage.getItem('customerId')
			const response = await this._exclusionZoneRepository.getAll(customerId!)
			this.exclusionZones.value = response
		} catch {
			this._addMessage({ description: 'Erro ao buscar zonas de exclusão', type: 'error' })
		}
	}

	loadExclusionZone(exclusionZoneId: string) {
		const exclusionZone = this.exclusionZones.value?.find(
			(alertSettings) => alertSettings.id === exclusionZoneId,
		)
		if (exclusionZone) {
			this.exclusionZone.value = { ...exclusionZone } as any
			this.openForm.value = true
		} else {
			this._addMessage({
				description: 'Erro ao recuperar dados de zona de exclusão',
				type: 'error',
			})
		}
	}

	exportDataTable() {
		const columns = [
			{ title: 'Codigo', key: 'code' },
			{ title: 'Fazenda', key: 'monitoredAreaName' },
			{ title: 'Área da zona', key: 'zonesAreaLabel' },
			{ title: 'Número de áreas', key: 'zoneCount' },
		]

		ExportDataProvider.downloadCSV(
			this.exclusionZones.value!,
			columns,
			`Zonas de exclusão ${new Date().toISOString()}`,
		)
	}

	private _onSuccessCreateExclusionZone() {
		this.openForm.value = false
		this._addMessage({
			description: 'Zona de exclusão cadastrada com sucesso!',
			type: 'success',
		})

		this.goToExclusionZonePage()
	}

	private _onSuccessUpdateExclusionZone() {
		this.openForm.value = false
		this._addMessage({
			description: 'Zona de exclusão editada com sucesso!',
			type: 'success',
		})

		this.goToExclusionZonePage()
	}

	private _onErrorCreateUpdateExclusionZone(errorMessage: string) {
		this._addMessage({
			description: errorMessage,
			type: 'error',
		})
	}

	async createExclusionZone(drawnExclusionZones?: FeatureCollection) {
		if (!drawnExclusionZones) return
		this.fetching.value = true

		const geometries: Geometry[] = drawnExclusionZones.features.map((feature) => feature.geometry)

		try {
			await this._exclusionZoneRepository.create({
				...this.exclusionZone.value,
				zones: geometries,
			} as ExclusionZone)
			this._onSuccessCreateExclusionZone()
		} catch (error) {
			this._onErrorCreateUpdateExclusionZone((error as Error).message)
		} finally {
			this.fetching.value = false
		}
	}

	async updateExclusionZone(drawnExclusionZones?: FeatureCollection) {
		if (!drawnExclusionZones) return
		this.fetching.value = true

		const geometries: Geometry[] = drawnExclusionZones.features.map((feature) => feature.geometry)
		try {
			await this._exclusionZoneRepository.update({
				...this.exclusionZone.value,
				zones: geometries,
			} as ExclusionZone)
			this._onSuccessUpdateExclusionZone()
		} catch (error) {
			this._onErrorCreateUpdateExclusionZone((error as Error).message)
		} finally {
			this.fetching.value = false
		}
	}

	getAlertSettingsToDelete() {
		if (!this.exclusionZones.value) return []
		return this.exclusionZones.value
			.filter((exclusionZone) =>
				this.registry.entriesToDelete.value.includes(exclusionZone.id as never),
			)
			.map((exclusionZone) => exclusionZone.description)
	}

	async deleteAlertSettings() {
		try {
			await this._exclusionZoneRepository.delete(this.registry.entriesToDelete.value)

			this.fetching.value = false
			this.showDeletePopup.value = false
			this.registry.entriesToDelete.value = []
			this.getExclusionZones()
			this._addMessage({
				description: 'Zonas de exclusão excluídas com sucesso!',
				type: 'info',
			})
		} catch {
			this._addMessage({ description: 'Erro ao excluir zonas de exclusão', type: 'error' })
		}
	}

	isFormValid = computed(() => {
		const { success } = ExclusionZoneFormSchema.safeParse(this.exclusionZone.value)

		return success
	})

	goToZonesPage() {
		router.push({ name: 'Zonas' })
	}

	goToExclusionZonePage() {
		router.push({ name: 'exclusion-zone' })
	}

	paintDrawnExclusionZone(drawnExclusionZones: FeatureCollection) {
		drawnExclusionZones.features.forEach(
			(feature) =>
				(feature.properties = { ...feature.properties, cor: '#F82027', preenchimento: 0.5 }),
		)

		return drawnExclusionZones
	}
}
